import React, { useState, useCallback } from "react"
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { getPayload, useAppThunkDispatch } from "redux/store";
import { useSelector } from "react-redux";
import { Market } from "model/market";
import { getLeadsOptimized } from "redux/selector";
import * as Icons from "react-feather";
import { styles } from "./styles";
import { SubscriptionsTable } from "./subscriptions-table";
import { updateMarketSubscriptionsNoLoading as updateMarketSubscriptions } from "redux/slice/market-report-subscriptions";
import { MarketReportSubscription, NewMarketReportSubscription } from "model/market-report-subscription";
import { FindSelect } from "component/shared/find-select";
import { MarketReportType } from "type/market-report-type";
import { SaveButton } from "component/shared/save-button";
import { fetchLeads, createLead } from "redux/slice/leads";
import { LeadStatus } from "type/lead-status";

interface Props extends WithStyles<typeof styles> {
	market: Market,
}

interface Props extends WithStyles<typeof styles> {
	market: Market,
}

const Component: React.FC<Props> = ({ market, classes = {} }) => {
	// Redux hooks
	const leads = useSelector(getLeadsOptimized).filter(lead => lead.status.active);
	const dispatch = useAppThunkDispatch();

	// Local state
	const [term, setTerm] = useState("");
	const [listingReport, setListingReport] = useState(true);
	const [openHomeReport, setOpenHomeReport] = useState(true);
	const [marketReport, setMarketReport] = useState(true);
	const [selectedLeads, setSelectedLeads] = useState<{id?: number | string, value: string}[]>([]);
	const [resetSelect, setResetSelect] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [saving, setSaving] = useState(false);

	const save = useCallback(async (subscriptions: (MarketReportSubscription | NewMarketReportSubscription)[]) => {
		setSaving(true);
		await dispatch(updateMarketSubscriptions({market, subscriptions}));
		setSaving(false);
		setResetSelect(true);
	}, [dispatch, market]);

	const add = useCallback(async () => {
		const subscriptions: (MarketReportSubscription | NewMarketReportSubscription)[] = market.marketReportSubscriptions ? [...market.marketReportSubscriptions] : [];
		for (const selected of selectedLeads) {
			const leadId = selected.id as number
			let lead = leads.find(lead => {
				if (leadId) {
					return lead.id === leadId;
				} else {
					return lead.email === selected.value;
				}
			});
			if (!lead) {
				const result = await dispatch(createLead({lead:{
					email: selected.value,
					status: LeadStatus.ACTIVE,
					label: selected.value,
				}}));
				const leadResource = (result as any).payload;
				lead = leads.find(lead => lead.id === (leadResource as any).id);
			}
			if (lead) {
				{
					const i = subscriptions.findIndex((item) => lead && item.lead.id === lead.id && item.report.type === MarketReportType.LISTING);
					if (i >= 0) {
						subscriptions[i].inactive = !listingReport;
					} else if (lead && market.listingReport && market.listingReportSupported) {
						subscriptions.push({
							lead: lead,
							inactive: !listingReport,
							report: market.listingReport,
						});
					}
				}
				{
					const i = subscriptions.findIndex((item) => lead && item.lead.id === lead.id && item.report.type === MarketReportType.OPEN_HOMES);
					if (i >= 0) {
						subscriptions[i].inactive = !openHomeReport;
					} else if (lead && market.openHomesReport && market.openHomesReportSupported) {
						subscriptions.push({
							lead: lead,
							inactive: !openHomeReport,
							report: market.openHomesReport,
						});
					}
				}
				{
					const i = subscriptions.findIndex((item) => lead && item.lead.id === lead.id && item.report.type === MarketReportType.MARKET);
					if (i >= 0) {
						subscriptions[i].inactive = !marketReport;
					} else if (lead && market.marketReport && market.marketReportSupported) {
						subscriptions.push({
							lead: lead,
							inactive: !marketReport,
							report: market.marketReport,
						});
					}
				}
			}
		};
		save(subscriptions);
	}, [selectedLeads, leads, market, listingReport, openHomeReport, marketReport, dispatch, save]);

	const subscribeAlertText = "Alerts and report pages are not available for Markets using manual listing numbers, date ranges or open-only fields.";

	return (
		<Mui.Grid container direction="column" spacing={2}>
			<Mui.Grid item>
				<Mui.Typography variant="h3">Add Leads</Mui.Typography>
			</Mui.Grid>
			<Mui.Grid item >
				<Mui.Grid container direction="row" wrap="nowrap" spacing={2}>
					<Mui.Grid item xs={9} style={{backgroundColor: "#f2f2f2", borderRadius: 5}}>
						<FindSelect 
							items={leads.map((lead) => ({
									id: lead.id,
									value: lead.label,
									label: lead.label,
								})
							)}
							includedItems={market.marketReportSubscriptions?.map((value) => ({
									id: value.lead.id,
									value: value.lead.label,
									label: value.lead.label,
								})
							)}
							includedItemsLabel="Update"
							reset={resetSelect}
							onUpdate={(selected, resetSelect) => {
								setSelectedLeads(selected);
								setResetSelect(!!resetSelect);
							}}
						/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Grid container direction="column" wrap="nowrap">
							<Mui.Grid item>
								<Mui.Typography>Receive:</Mui.Typography>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Grid container alignItems="center" wrap="nowrap">
									{market.listingReportSupported ? (
										<Mui.Checkbox
											checked={listingReport}
											onClick={() => setListingReport(!listingReport)}
										/>
									): 
										<Mui.Tooltip 
											title={subscribeAlertText}
										>
											<Icons.AlertCircle className={classes.alertCircle}/>
										</Mui.Tooltip>
									}
									<Mui.Typography>Listing Report</Mui.Typography>
								</Mui.Grid>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Grid container alignItems="center" wrap="nowrap">
									{market.openHomesReportSupported ? (
										<Mui.Checkbox
											checked={openHomeReport}
											onClick={() => setOpenHomeReport(!openHomeReport)}
										/>
									): 
										<Mui.Tooltip 
											title={subscribeAlertText}
										>
											<Icons.AlertCircle className={classes.alertCircle}/>
										</Mui.Tooltip>
									}
									<Mui.Typography>Open House Reports</Mui.Typography>
								</Mui.Grid>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Grid container alignItems="center" wrap="nowrap">
									{market.marketReportSupported ? (
										<Mui.Checkbox
											checked={marketReport}
											onClick={() => setMarketReport(!marketReport)}
										/>
									): 
									<Mui.Tooltip 
										title={subscribeAlertText}
									>
										<Icons.AlertCircle className={classes.alertCircle}/>
									</Mui.Tooltip>
									}
									<Mui.Typography>Market Reports</Mui.Typography>
								</Mui.Grid>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Tooltip title="Select one or more leads" disableHoverListener={selectedLeads.length > 0}>
									<span>
									<SaveButton
										onClick={() => {
											setSubmitted(true);
											setSaving(true);
											add();
										}} 
										loading={saving} 
										label={{
											primary: "Subscribe", 
											inProgress: `Saving... ${market.marketReportSubscriptions && market.marketReportSubscriptions.length > 100 ? "(This may take a minute)" : ""}`, 
											completed: "Saved" 
										}}
										disabled={selectedLeads.length === 0}
										snackBar
										duration={3000}
										onReset={() => setSubmitted(false)}
									/>
									</span>
								</Mui.Tooltip>
							</Mui.Grid>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.Grid>
			<Mui.Grid item>
				{market.marketReportSubscriptions && market.marketReportSubscriptions.length > 0 && 
					<SubscriptionsTable saving={saving} market={market} onSave={(subscriptions) => save(subscriptions)} />
				}
			</Mui.Grid>
		</Mui.Grid>
	);
};

export const MarketEditSubscriptions = withStyles(Component, styles);